import * as React from 'react';
import { useLocation } from 'react-router';

import { ApolloCache, useMutation } from '@apollo/client';
import { isEmpty } from 'lodash';

import {
  GetItemDataByIdQuery,
  WishlistList,
  WishlistMutateListsMutation,
  WishlistMutateListsMutationVariables,
} from '@notino/shared/definitions/types';

import getItemDataByIdQuery from '@components/Universals/ProductItem/queries/itemDataById.graphql';
import { useIsSkinAnalyzerVisible } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/SkinAnalyser/hooks/useIsSkinAnalyzerVisible';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useWishlistId } from '@containers/WishList/hooks/useWishlistId';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import wishlistMutateListsMutation from '../queries/wishlistMutateLists.graphql';
import { trackExponeaWishlistUpdate } from '../tracking/exponea';
import {
  handleTrackWishlistOperation,
  WishlistActionLocation,
} from '../tracking/gtm';
import { updateWishlistedCache } from '../updateWishlistedCache';
import { useWishlistLists } from '../useWishlistLists';

import { useIsWishlistEnabled } from './useIsWishlistEnabled';

export const publishUpdateWishlistCountEvent = () => {
  const event = new CustomEvent('header:wishlist:overview:update');
  document.dispatchEvent(event);
};

interface IUseWishlistActions {
  productId: string;
  onCompleted?: (
    products: { productId: string; wishlisted: boolean }[]
  ) => void;
  selectionMode?: boolean;
}

export const useWishlistActionsFactory = ({
  onCompleted,
  selectionMode,
}: Pick<IUseWishlistActions, 'onCompleted' | 'selectionMode'>) => {
  const { getTimeFromInit } = useTrackingContext();
  const pdContext = useProductDetailContext(false);
  const flags = useFeatureFlags();
  const shouldShowSkinAnalyzer = useIsSkinAnalyzerVisible();
  const isWishlistAllowed = useIsWishlistEnabled();
  const { listId } = useWishlistId();
  const { pathname } = useLocation();
  const isWishlistPage = pathname.includes('/wishlist');

  const [mutateLists, { loading }] = useMutation<
    WishlistMutateListsMutation,
    WishlistMutateListsMutationVariables
  >(wishlistMutateListsMutation);

  const handleWishlistClick = React.useCallback(
    async ({
      productIds,
      addToLists,
      removeFromLists,
      lists,
      actionLocation,
      trackingPromoLabels = [],
      listName,
      listPosition,
    }: {
      productIds: string[];
      addToLists: { id: string }[];
      removeFromLists: { id: string }[];
      lists: WishlistList[];
      actionLocation: WishlistActionLocation;
      trackingPromoLabels?: string[];
      listName?: string;
      listPosition?: number;
    }) => {
      const productId = productIds[0];

      const addedToWishlist =
        isWishlisted({
          addToLists,
          removeFromLists,
          lists,
          productId,
        }) && removeFromLists.length === 0;

      const productsWishlisted = productIds.map((id) => ({
        productId: id,
        wishlisted: isWishlisted({
          productId: id,
          addToLists,
          removeFromLists,
          lists,
        }),
      }));

      const isRelocated =
        removeFromLists.length !== 0 && addToLists.length !== 0;

      const { data } = await mutateLists({
        variables: {
          productIds,
          addToList: addToLists.map((list) => list.id),
          removeFromList: removeFromLists.map((list) => list.id),
        },
        update: (cache: ApolloCache<object>) => {
          updateWishlistedCache({
            cache,
            products: productsWishlisted,
          });

          const vpProducts = productIds.map((id) => {
            return cache.readQuery<GetItemDataByIdQuery>({
              query: getItemDataByIdQuery,
              variables: { id },
            })?.vpProductById;
          });

          const isCurrentProductDetail =
            pdContext?.currentVariant.webId === productId;

          if (vpProducts.length > 0 || isCurrentProductDetail) {
            handleTrackWishlistOperation({
              listsAddedCount: addToLists.length,
              lists,
              productCount: productIds.length,
              removeFromLists,
              isWishlistPage,
              isRelocated,
              addedToWishlist,
              shouldShowSkinAnalyzer,
              selectionMode,
              vpProducts,
              timing: getTimeFromInit(),
              flags,
              actionLocation,
              additionalPromoLabels: trackingPromoLabels,
              product: isCurrentProductDetail ? pdContext.product : null,
              variant: isCurrentProductDetail
                ? pdContext?.currentVariant
                : null,
              additionalGtm: {
                list_name: listName,
                list_position: listPosition,
                ...(isWishlistPage && {
                  wishlist_type:
                    listId === useWishlistLists.DEFAULT_LIST_ID
                      ? 'default'
                      : 'custom',
                }),
              },
              engravingAvailable: pdContext?.engravingAvailable,
              tryItFirstAvailable: pdContext?.tryItFirstAvailable,
              modifaceVariants: isEmpty(pdContext?.modiface.hair)
                ? pdContext?.modiface.makeup
                : pdContext.modiface.hair,
            });
          }
        },
      });

      const wishlistProductIds = data?.WishlistMutateLists?.lists?.flatMap(
        (item) => item.products
      );

      trackExponeaWishlistUpdate({
        action: addedToWishlist ? 'add' : 'remove',
        productId: Number(productId),
        wishlistProductIds: wishlistProductIds.map(Number),
      });

      publishUpdateWishlistCountEvent();
      onCompleted?.(productsWishlisted);
    },
    [
      mutateLists,
      onCompleted,
      getTimeFromInit,
      pdContext,
      flags,
      shouldShowSkinAnalyzer,
      isWishlistPage,
      listId,
      selectionMode,
    ]
  );

  return React.useMemo(
    () => ({
      handleWishlistClick,
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading]
  );
};

export const useWishlistActions = ({
  productId,
  onCompleted,
}: IUseWishlistActions) => {
  const { handleWishlistClick, isWishlistAllowed, loading } =
    useWishlistActionsFactory({ onCompleted });

  return React.useMemo(
    () => ({
      handleWishlistClick: ({
        actionLocation,
        trackingPromoLabels = [],
        addToLists,
        removeFromLists,
        lists,
      }: {
        actionLocation: WishlistActionLocation;
        trackingPromoLabels: string[];
        addToLists: { id: string }[];
        removeFromLists: { id: string }[];
        lists: WishlistList[];
      }) => {
        return handleWishlistClick({
          productIds: [productId],
          addToLists,
          removeFromLists,
          lists,
          actionLocation,
          trackingPromoLabels,
        });
      },
      isWishlistAllowed,
      loading,
    }),
    [handleWishlistClick, isWishlistAllowed, loading, productId]
  );
};

const isWishlisted = ({
  addToLists,
  removeFromLists,
  lists,
  productId,
}: {
  addToLists: { id: string }[];
  removeFromLists: { id: string }[];
  lists: WishlistList[];
  productId: string;
}) => {
  if (addToLists.length) {
    return true;
  }
  const wasWishlistedInListIds = lists
    .filter((list) => list.products.includes(Number(productId)))
    .map((list) => list.id);

  const areAllRemoved = wasWishlistedInListIds.every((listId) =>
    removeFromLists.some(({ id }) => id === listId)
  );

  return !areAllRemoved;
};
